import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IReportDocument } from '@shared/interfaces';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';

enum Status {
    initial,
    loaded
}

@Component({
    selector: 'app-drop-zone',
    templateUrl: './drop-zone.component.html',
    styleUrls: ['./drop-zone.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropZoneComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DropZoneComponent implements ControlValueAccessor {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    dragging = false;
    currentStatus = Status.initial;
    @Output() filesChanged = new EventEmitter<(File | IReportDocument)[]>();
    @Output() removed = new EventEmitter<string>();
    @Output() itemHandler = new EventEmitter<IReportDocument>();
    @Input() value: Array<File | IReportDocument> = [];
    @Input() accept: string;
    @Input() acceptType: string;
    @Input() multiple = true;
    @Input() disabled = false;
    @Input() readonly = false;
    @Input() title: string;
    @Input() definition: string;
    @Input() required = false;

    @HostBinding('class.required') get isRequired() {
        return this.required;
    }

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    get isLoaded() {
        return this.currentStatus === Status.loaded;
    }

    get isInitial() {
        return this.currentStatus === Status.initial;
    }

    filesChange(event: Event | CustomEvent) {
        const fileList: Array<File> = (event.target as any)?.files?.length
            ? Array.from((event.target as any)?.files)
            : (event as CustomEvent).detail;
        if (fileList && fileList.length) {
            this.value = fileList.filter(
                e => !this.accept || e.type.startsWith(this.acceptType || `application/${this.accept.replace('.', '')}`)
            );
            this.currentStatus = this.value.length ? Status.loaded : Status.initial;
            this.filesChanged.emit(this.value);
            this.propagateChange(this.value);
        } else if (fileList === null) {
            this.value = [];
            this.currentStatus = this.value.length ? Status.loaded : Status.initial;
            this.filesChanged.emit(this.value);
            this.propagateChange(this.value);
        }
    }

    removeFile(file: File | IReportDocument) {
        this.value = this.value.filter(e => e !== file);
        this.removed.emit(file?.name);
        if (!this.value.length) {
            this.currentStatus = Status.initial;
            this.value = null;
        }
        this.filesChanged.emit(this.value);
        this.propagateChange(this.value);
    }

    writeValue(value: (File | IReportDocument)[]) {
        if (value && value.length) {
            this.value = value;
            this.currentStatus = Status.loaded;
        } else {
            this.currentStatus = Status.initial;
            this.value = [];
            this.propagateChange(this.value);

            if (this.fileInput?.nativeElement?.value) {
                this.fileInput.nativeElement.value = null;
            }
        }
    }

    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {
        /* Form control touched handler */
    }

    propagateChange = (_: any) => {
        // ControlValueAccessor callback placeholder
    };

    isIReportDocument(file: File | IReportDocument): file is IReportDocument {
        return !(file instanceof File);
    }
}
